// Schedules: the design's schedule store (state/schedules.tsx) rebuilt on the
// backend. Opening hours are facility_schedules (one row per day holding any
// number of open–close `windows`, item 182), doctor shifts are
// doctor_schedules rows, clinic holidays are organization_holidays (one row per
// date, optionally a half day with a note, items 177–178; consecutive dates
// with the same name, locations, hours and note show as one holiday) and
// doctor leave is schedule_exceptions with a doctor, a covering doctor and any
// number of time ranges (items 180, 183). A shift is either 'fixed' (the
// doctor sits at the clinic; patients book these times) or 'on_call' (the
// doctor can be called in; a booking against it is a request the clinic
// confirms with the doctor) — doctor_schedules.schedule_type, as in the old
// SchedulesPage.
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet } from "../crm/contactsKit";
import { DAY, ymd } from "../../lib/format";
import { apiSend, loadAppointments, loadDoctors } from "../appointments/appointmentsData";
import { fetchApi } from "../marketing/access";

export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
export const LEAVE_KINDS = ["Leave", "Conference", "Sick", "Other"];
/** Design weekday → day_of_week (0 = Sunday). */
export const dowOf = (day) => (WEEKDAYS.indexOf(day) + 1) % 7;
const dayOfDow = (dow) => WEEKDAYS[(Number(dow) + 6) % 7];
export const mins = (time) => Number(String(time).slice(0, 2)) * 60 + Number(String(time).slice(3, 5));
const hm = (time) => (time ? String(time).slice(0, 5) : "");
const isoOf = (value) => (value ? (/^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? String(value) : ymd(new Date(value).getTime())) : "");
export const fromIso = (value) => { const [y, m, d] = String(value).split("-").map(Number); return new Date(y, m - 1, d).getTime(); };
export const clock = (value) => { const [h, m] = value.split(":").map(Number); return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
const weekdayAt = (ts) => WEEKDAYS[(new Date(ts).getDay() + 6) % 7];
const emptyWeek = (fill) => Object.fromEntries(WEEKDAYS.map((d) => [d, fill()]));
const json = (v) => { if (typeof v !== "string") return v; try { return JSON.parse(v); } catch (_) { return null; } };
const truthy = (v) => v === true || v === 1 || v === "1";

/* ---------- Row mapping ---------- */

/** A facility_schedules row's opening windows: the stored list, else open/close with the break. */
export function windowsOfRow(row) {
  const list = json(row.windows);
  if (Array.isArray(list)) return list.map((w) => ({ start: hm(w.open), end: hm(w.close) })).filter((w) => w.start && w.end).sort((a, b) => a.start.localeCompare(b.start));
  if (!truthy(row.is_open)) return [];
  return row.break_start && row.break_end
    ? [{ start: hm(row.open_time), end: hm(row.break_start) }, { start: hm(row.break_end), end: hm(row.close_time) }]
    : [{ start: hm(row.open_time), end: hm(row.close_time) }];
}

export function toLocation(facility, rows, doctorRows) {
  const week = emptyWeek(() => null);
  rows.forEach((row) => {
    if (row.is_active === 0) return;
    const shifts = windowsOfRow(row);
    week[dayOfDow(row.day_of_week)] = { open: shifts.length > 0, shifts };
  });
  // The location's own slot length (item 184); before one is saved, the length most of its doctor shifts use.
  const own = Number(facility.slot_duration_minutes ?? rows.find((r) => r.slot_duration_minutes != null)?.slot_duration_minutes) || null;
  const counts = {};
  doctorRows.filter((r) => Number(r.facility_id) === Number(facility.id)).forEach((r) => { const n = Number(r.slot_duration_minutes) || 30; counts[n] = (counts[n] || 0) + 1; });
  const slot = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return { facilityId: facility.id, facility: facility.name, address: [facility.address, facility.city].filter(Boolean).join(", "), week, known: rows.length > 0, slotMinutes: own ?? (slot ? Number(slot[0]) : null) };
}

/** Opening hours → the bulk endpoint's body: each day's windows, any count (item 182). */
export function bulkHoursBody(loc) {
  return {
    facility_id: Number(loc.facilityId),
    schedules: WEEKDAYS.map((day) => {
      const d = loc.week[day] || { open: false, shifts: [] };
      return { day_of_week: dowOf(day), windows: d.open ? d.shifts.map((w) => ({ open: w.start, close: w.end })) : [] };
    }),
    ...(loc.slotMinutes ? { slot_duration_minutes: loc.slotMinutes } : {}),
  };
}

export function groupHolidays(rows, facilityName) {
  const sorted = [...rows].sort((a, b) => isoOf(a.holiday_date).localeCompare(isoOf(b.holiday_date)));
  const out = [];
  sorted.forEach((row) => {
    const date = isoOf(row.holiday_date);
    const all = !!row.applies_to_all_facilities;
    const ids = all ? [] : (Array.isArray(row.facility_ids) ? row.facility_ids : []).map(Number).sort();
    // Half day: start_time–end_time are the hours the clinic is CLOSED (item 177).
    const half = truthy(row.is_half_day) && row.start_time && row.end_time ? { start: hm(row.start_time), end: hm(row.end_time) } : null;
    const note = row.note || "";
    const key = `${row.holiday_name}|${all ? "all" : ids.join(",")}|${half ? `${half.start}-${half.end}` : "closed"}|${note}`;
    const last = out[out.length - 1];
    if (last && last.key === key && fromIso(last.endDate || last.date) + DAY === fromIso(date)) {
      last.endDate = date;
      last.rows.push(row);
      return;
    }
    out.push({ id: `hol-${row.id}`, key, name: row.holiday_name, date, facilities: all ? "all" : ids.map((id) => facilityName(id)), facilityIds: ids, type: half ? "Half day" : "Closed", ...(half ? { hours: half } : {}), note, rows: [row] });
  });
  return out;
}

export function toLeave(row) {
  const start = isoOf(row.exception_date);
  const kind = LEAVE_KINDS.includes(row.title) ? row.title : row.exception_type === "leave" ? "Leave" : "Other";
  // Several ranges in a day (item 183); older rows hold one in special_start/end_time.
  const ranges = json(row.ranges);
  const partials = truthy(row.is_full_day) ? [] : Array.isArray(ranges) && ranges.length
    ? ranges.map((r) => ({ start: hm(r.start), end: hm(r.end) }))
    : row.special_start_time && row.special_end_time ? [{ start: hm(row.special_start_time), end: hm(row.special_end_time) }] : [];
  return {
    id: `leave-${row.id}`,
    rowId: row.id,
    doctorId: row.doctor_id,
    doctor: row.doctor_name || "Doctor",
    kind,
    start,
    end: isoOf(row.end_date) || start,
    ...(partials.length ? { partials } : {}),
    coveringDoctorId: row.covering_doctor_id || null,
    coveringDoctor: row.covering_doctor_name || null,
    notified: truthy(row.patients_notified),
    note: row.description || "",
    raw: row,
  };
}

/** A holiday → one organization_holidays body per date. */
export function holidayRowBodies(item) {
  const out = [];
  for (let d = fromIso(item.date); d <= fromIso(item.endDate || item.date); d += DAY) {
    out.push({
      holiday_name: item.name, holiday_date: ymd(d),
      applies_to_all_facilities: item.facilities === "all", facility_ids: item.facilities === "all" ? null : item.facilityIds, is_recurring: false,
      is_half_day: item.type === "Half day",
      start_time: item.type === "Half day" ? `${item.hours.start}:00` : null,
      end_time: item.type === "Half day" ? `${item.hours.end}:00` : null,
      note: item.note || null,
    });
  }
  return out;
}

/** A leave → the exceptions body: full day, or its ranges; the covering doctor (items 180, 183). */
export function leaveBodyOf(item) {
  const ranges = item.partials?.length ? item.partials.map((p) => ({ start: `${p.start}:00`, end: `${p.end}:00` })) : null;
  return {
    doctor_id: Number(item.doctorId),
    exception_type: "leave",
    exception_date: item.start,
    end_date: item.end,
    is_full_day: !ranges,
    ranges,
    special_start_time: ranges ? ranges[0].start : null,
    special_end_time: ranges ? ranges[0].end : null,
    covering_doctor_id: item.coveringDoctorId ? Number(item.coveringDoctorId) : null,
    title: item.kind,
    description: item.note || null,
  };
}

/** A stored holiday row → the body that creates it again (undo). */
const holidayRestoreBody = (row) => ({ holiday_name: row.holiday_name, holiday_date: isoOf(row.holiday_date), is_optional: !!row.is_optional, applies_to_all_facilities: !!row.applies_to_all_facilities, facility_ids: row.facility_ids || null, is_recurring: !!row.is_recurring, is_half_day: truthy(row.is_half_day), start_time: row.start_time || null, end_time: row.end_time || null, note: row.note || null });

/* ---------- Hook ---------- */

/**
 * Everything the Schedules screen reads, plus the writes. Writes resolve to
 * { ok, message } and reload the data.
 * facilities: the location switcher's facilities [{ id, name }].
 */
export function useScheduleData(facilities) {
  const [raw, setRaw] = useState(null);
  const [failure, setFailure] = useState(null);
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((v) => v + 1), []);
  useEffect(() => {
    let live = true;
    (async () => {
      const [fac, ...lists] = await Promise.all([
        apiGet("/api/facilities", { is_active: "true" }),
        fetchApi("/api/facility-schedules"),
        fetchApi("/api/doctor-schedules", { params: { is_active: "true" } }),
        fetchApi("/api/doctor-schedules/holidays"),
        fetchApi("/api/doctor-schedules/exceptions"),
      ]);
      const staff = await loadDoctors();
      if (!live) return;
      // Hours, shifts, holidays and leave that didn't load would read as "none"
      // (and invite re-entering them), so a failure is shown instead.
      const failed = lists.find((r) => !r.ok);
      if (failed) { setRaw(null); setFailure(failed); return; }
      const [hours, shifts, holidays, exceptions] = lists.map((r) => r.data?.data || []);
      setFailure(null);
      setRaw({ facilityRows: fac?.success ? fac.data || [] : [], hours, shifts, holidays, exceptions, staff });
    })();
    return () => { live = false; };
  }, [version]);

  const facilityName = useCallback((id) => facilities.find((f) => Number(f.id) === Number(id))?.name || raw?.facilityRows.find((f) => Number(f.id) === Number(id))?.name || "Location", [facilities, raw]);

  const data = useMemo(() => {
    if (!raw) return null;
    const locations = facilities.map((f) => {
      const full = raw.facilityRows.find((x) => Number(x.id) === Number(f.id)) || f;
      return toLocation({ ...full, id: f.id, name: f.name }, raw.hours.filter((r) => Number(r.facility_id) === Number(f.id)), raw.shifts);
    });
    const people = new Map(raw.staff.map((d) => [d.id, { doctorId: d.id, doctor: d.name, week: emptyWeek(() => []) }]));
    raw.shifts.forEach((r) => {
      if (!people.has(r.doctor_id)) people.set(r.doctor_id, { doctorId: r.doctor_id, doctor: r.doctor_name || "Doctor", week: emptyWeek(() => []) });
      people.get(r.doctor_id).week[dayOfDow(r.day_of_week)].push({ id: r.id, facilityId: r.facility_id, facility: r.facility_name || facilityName(r.facility_id), start: hm(r.start_time), end: hm(r.end_time), onCall: r.schedule_type === "on_call", raw: r });
    });
    const doctors = [...people.values()];
    doctors.forEach((p) => WEEKDAYS.forEach((d) => p.week[d].sort((a, b) => a.start.localeCompare(b.start))));
    const holidays = groupHolidays(raw.holidays, facilityName);
    const leaves = raw.exceptions.filter((r) => r.doctor_id && !["rejected", "cancelled"].includes(r.status)).map(toLeave);
    return { locations, doctors, holidays, leaves };
  }, [raw, facilities, facilityName]);

  const schedule = useMemo(() => {
    if (!data) return null;
    const { locations, doctors, holidays, leaves } = data;
    const doctorById = (id) => doctors.find((p) => String(p.doctorId) === String(id));
    const run = async (steps) => {
      for (const step of steps) {
        const r = await step();
        if (!r.ok) { reload(); return { ok: false, message: r.message }; }
      }
      reload();
      return { ok: true };
    };
    const shiftBody = (doctorId, day, block, base = {}) => ({
      facility_id: Number(block.facilityId),
      doctor_id: Number(doctorId),
      day_of_week: dowOf(day),
      start_time: `${block.start}:00`,
      end_time: `${block.end}:00`,
      slot_duration_minutes: base.slot_duration_minutes || locations.find((l) => Number(l.facilityId) === Number(block.facilityId))?.slotMinutes || 30,
      schedule_type: block.onCall === undefined ? base.schedule_type || "fixed" : block.onCall ? "on_call" : "fixed",
      ...(base.consultation_fee != null ? { consultation_fee: base.consultation_fee } : {}),
      ...(base.room_number ? { room_number: base.room_number } : {}),
    });
    /** Steps that turn one doctor-day into `blocks`: deletes, then edits, then new shifts. */
    const daySteps = (doctorId, day, blocks) => {
      const current = doctorById(doctorId)?.week[day] || [];
      const keep = new Set(blocks.filter((b) => b.id).map((b) => b.id));
      const steps = current.filter((b) => !keep.has(b.id)).map((b) => () => apiSend("delete", `/api/doctor-schedules/${b.id}`));
      blocks.filter((b) => b.id).forEach((b) => {
        const was = current.find((c) => c.id === b.id);
        if (was && (was.start !== b.start || was.end !== b.end || Number(was.facilityId) !== Number(b.facilityId) || !!was.onCall !== !!b.onCall)) {
          steps.push(() => apiSend("put", `/api/doctor-schedules/${b.id}`, { facility_id: Number(b.facilityId), start_time: `${b.start}:00`, end_time: `${b.end}:00`, schedule_type: b.onCall ? "on_call" : "fixed" }));
        }
      });
      blocks.filter((b) => !b.id).forEach((b) => steps.push(() => apiSend("post", "/api/doctor-schedules", shiftBody(doctorId, day, b, b.raw))));
      return steps;
    };

    return {
      locations, doctors, holidays, leaves,
      doctorById,
      validateDoctorDay: (day, blocks) => {
        const issues = [];
        blocks.forEach((block, index) => {
          const loc = locations.find((item) => Number(item.facilityId) === Number(block.facilityId));
          const hours = loc?.week[day];
          const start = mins(block.start); const end = mins(block.end);
          if (hours && (!hours.open || !hours.shifts.some((window) => start >= mins(window.start) && end <= mins(window.end)))) issues.push(`${day} ${clock(block.end)} is outside ${loc.facility} opening hours.`);
          if (end <= start) issues.push(`${day} block must end after it starts.`);
          blocks.slice(index + 1).forEach((other) => { if (block.start < other.end && other.start < block.end) issues.push(`${day} blocks overlap at ${clock(other.start)}.`); });
        });
        return issues;
      },
      availability: (doctorId, date) => {
        const ts = fromIso(date); const day = weekdayAt(ts);
        const person = doctorById(doctorId);
        const holiday = holidays.find((item) => ts >= fromIso(item.date) && ts <= fromIso(item.endDate || item.date) && (item.facilities === "all" || item.facilityIds.some((id) => person?.week[day].some((block) => Number(block.facilityId) === id))));
        const leave = leaves.find((item) => String(item.doctorId) === String(doctorId) && ts >= fromIso(item.start) && ts <= fromIso(item.end));
        if (leave && !leave.partials?.length) return { blocks: [], reason: "Leave", leave };
        if (holiday?.type === "Closed") return { blocks: [], reason: "Closed" };
        let blocks = person?.week[day] ?? [];
        // A half day closes its hours; part-day leave takes its ranges out of every day it covers.
        if (holiday?.type === "Half day" && holiday.hours) blocks = subtractRanges(blocks, [holiday.hours]);
        if (leave?.partials?.length) blocks = subtractRanges(blocks, leave.partials);
        return { blocks, reason: null };
      },

      /* Writes */
      // Hours and the location's slot length in one request (items 182, 184). Booking
      // slots are cut from each doctor shift's own length, so a changed length is
      // also written to the shifts at this location.
      updateLocation: (loc, slotChanged) => run([
        () => apiSend("post", "/api/facility-schedules/bulk", bulkHoursBody(loc)),
        ...(slotChanged && loc.slotMinutes ? doctors.flatMap((p) => WEEKDAYS.flatMap((d) => p.week[d].filter((b) => Number(b.facilityId) === Number(loc.facilityId)).map((b) => () => apiSend("put", `/api/doctor-schedules/${b.id}`, { slot_duration_minutes: loc.slotMinutes })))) : []),
      ]),
      updateDoctor: (doctorId, day, blocks) => run(daySteps(doctorId, day, blocks)),
      setDoctorWeek: (doctorId, week) => run(WEEKDAYS.flatMap((day) => daySteps(doctorId, day, week[day]))),
      /** Re-creates shifts from a snapshot (undo after delete / clear). */
      restoreShifts: (doctorId, snapshot) => run(WEEKDAYS.flatMap((day) => (snapshot[day] || []).filter((b) => !doctorById(doctorId)?.week[day].some((c) => c.id === b.id)).map((b) => () => apiSend("post", "/api/doctor-schedules", shiftBody(doctorId, day, b, b.raw))))),
      addHoliday: (item) => run(holidayRowBodies(item).map((body) => () => apiSend("post", "/api/doctor-schedules/holidays", body))),
      removeHoliday: (item) => run(item.rows.map((row) => () => apiSend("delete", `/api/doctor-schedules/holidays/${row.id}`))),
      restoreHoliday: (item) => run(item.rows.map((row) => () => apiSend("post", "/api/doctor-schedules/holidays", holidayRestoreBody(row)))),
      /** Edit (item 178): dates kept are updated in place, new dates added, dropped dates removed. */
      updateHoliday: (old, item) => {
        const byDate = new Map(old.rows.map((row) => [isoOf(row.holiday_date), row]));
        const bodies = holidayRowBodies(item);
        const keep = new Set(bodies.map((b) => b.holiday_date));
        return run([
          ...bodies.map((body) => (byDate.has(body.holiday_date)
            ? () => apiSend("put", `/api/doctor-schedules/holidays/${byDate.get(body.holiday_date).id}`, (({ holiday_date, ...rest }) => rest)(body))
            : () => apiSend("post", "/api/doctor-schedules/holidays", body))),
          ...old.rows.filter((row) => !keep.has(isoOf(row.holiday_date))).map((row) => () => apiSend("delete", `/api/doctor-schedules/holidays/${row.id}`)),
        ]);
      },
      /** Resolves to { ok, message, rowId } — the new leave's id, for reassigning right after. */
      addLeave: async (item) => {
        const r = await apiSend("post", "/api/doctor-schedules/exceptions", leaveBodyOf(item));
        reload();
        return r.ok ? { ok: true, rowId: r.body?.data?.id ?? null } : { ok: false, message: r.message };
      },
      /** Same doctor: updated in place. Another doctor: a new leave, then the old one removed. */
      updateLeave: async (old, item) => {
        if (String(old.doctorId) === String(item.doctorId)) {
          const r = await run([() => apiSend("put", `/api/doctor-schedules/exceptions/${old.rowId}`, (({ doctor_id, ...rest }) => rest)(leaveBodyOf(item)))]);
          return { ...r, rowId: old.rowId };
        }
        const created = await apiSend("post", "/api/doctor-schedules/exceptions", leaveBodyOf(item));
        if (!created.ok) { reload(); return { ok: false, message: created.message }; }
        const r = await run([() => apiSend("delete", `/api/doctor-schedules/exceptions/${old.rowId}`)]);
        return { ...r, rowId: created.body?.data?.id ?? null };
      },
      removeLeave: (item) => run([() => apiSend("delete", `/api/doctor-schedules/exceptions/${item.rowId}`)]),
      restoreLeave: (item) => run([() => apiSend("post", "/api/doctor-schedules/exceptions", leaveBodyOf(item))]),
      /**
       * Message the patients booked in a holiday or leave with one of the clinic's
       * approved WhatsApp templates (item 179). The backend picks the bookings —
       * only those inside the closed hours for a half day or part-day leave — and
       * sends through the wallet-checked pipeline, at most 200 per request.
       * Resolves to { ok, message, data: { notified, total, failed, remaining } }.
       */
      notifyAffected: async ({ holiday, leave, templateId }) => {
        const body = holiday
          ? { holiday_id: holiday.rows[0].id, date_from: holiday.date, date_to: holiday.endDate || holiday.date, template_id: Number(templateId) }
          : { exception_id: leave.rowId, template_id: Number(templateId) };
        const r = await apiSend("post", "/api/doctor-schedules/notify-affected", body);
        reload();
        return r.ok ? { ok: true, data: r.body?.data || {} } : { ok: false, message: r.message };
      },
      /** Move the doctor's bookings in this leave to the covering doctor (item 181) → { ok, data: { moved, skipped } }. */
      reassignLeave: async (rowId, coveringDoctorId) => {
        const r = await apiSend("post", `/api/doctor-schedules/exceptions/${rowId}/reassign`, coveringDoctorId ? { covering_doctor_id: Number(coveringDoctorId) } : {});
        reload();
        return r.ok ? { ok: true, data: r.body?.data || { moved: 0, skipped: [] } } : { ok: false, message: r.message };
      },
    };
  }, [data, reload]);

  return { schedule, loading: !raw && !failure, failure, reload };
}

const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
/** Blocks [{ start, end, … }] with the ranges taken out. */
export function subtractRanges(blocks, ranges) {
  let out = blocks;
  for (const range of ranges) {
    const offStart = mins(range.start); const offEnd = mins(range.end);
    out = out.flatMap((block) => {
      const start = mins(block.start); const end = mins(block.end);
      if (offEnd <= start || offStart >= end) return [block];
      const remaining = [];
      if (start < offStart) remaining.push({ ...block, end: hhmm(offStart) });
      if (offEnd < end) remaining.push({ ...block, start: hhmm(offEnd) });
      return remaining;
    });
  }
  return out;
}

/**
 * Appointments that fall in a date range and, when `partials` is given, inside
 * those hours on any of its days — the same rule the backend uses to pick who
 * to notify or reassign.
 */
export function affectedIn(appointments, { start, end, facilityIds, doctorId, partials }) {
  const from = fromIso(start); const to = fromIso(end || start) + DAY;
  return (appointments || []).filter((item) => {
    if (item.start < from || item.start >= to || item.status === "Cancelled" || item.status === "No-show") return false;
    if (doctorId && String(item.doctorId) !== String(doctorId)) return false;
    if (facilityIds && !facilityIds.includes(Number(item.facilityId))) return false;
    if (!partials?.length) return true;
    const minute = new Date(item.start).getHours() * 60 + new Date(item.start).getMinutes();
    const length = Number(item.duration) || 30;
    return partials.some((part) => minute < mins(part.end) && minute + length > mins(part.start));
  });
}

/** Appointments between two ISO dates, for a drawer's impact note. */
export function useAppointmentsBetween(start, end, doctorId) {
  // { key, rows }: the dates the list was loaded for, so a changed date range
  // doesn't show the previous range's bookings until its own answer arrives.
  const [loaded, setLoaded] = useState({ key: null, rows: [] });
  const key = JSON.stringify([start || null, end || null, doctorId ?? null]);
  useEffect(() => {
    if (!start) return undefined;
    let live = true;
    loadAppointments({ from: fromIso(start), to: fromIso(end || start), doctorId, maxPages: 5 }).then((rows) => live && setLoaded({ key, rows: rows || [] }));
    return () => { live = false; };
  }, [start, end, doctorId]); // eslint-disable-line react-hooks/exhaustive-deps
  return start && loaded.key === key ? loaded.rows : EMPTY;
}
const EMPTY = [];

/** The clinic's approved WhatsApp templates that the appointment fields can fill (name, doctor, date, time, location, clinic). */
export async function loadNotifyTemplates() {
  const r = await apiGet("/api/waba/templates", { status: "approved", limit: 100 });
  return (r?.templates || [])
    .filter((t) => t.template_name && t.is_active !== 0 && t.is_active !== false)
    .map((t) => ({ id: t.id, name: t.template_name, body: t.body_text || "", vars: new Set(String(t.body_text || "").match(/\{\{\s*\d+\s*\}\}/g) || []).size }))
    .filter((t) => t.vars <= 6);
}
